/**
 * Tizen platform detection helpers.
 *
 * BU8000 and similar 2022 signage TVs report "Tizen 6.5" in the UA.
 * Parsing only the major digit (6) incorrectly classifies them as legacy Tizen (<= 6.0).
 * TEP uses webapis.avplay (avplayextension.js); legacy AU8000 uses webapis.epavplay.
 */

/**
 * @returns {number|null} Parsed Tizen version from UA (e.g. 6.5), or null if not Tizen.
 */
export function getTizenVersionFromUA() {
  if (navigator.userAgent.indexOf('Tizen') < 0) {
    return null;
  }
  var match = navigator.userAgent.match(/Tizen ([\d.]+)/);
  if (!match) {
    return null;
  }
  var version = parseFloat(match[1]);
  return isNaN(version) ? null : version;
}

/**
 * True when Tizen version is above 6.0 (6.5, 7, 8, …) — TEP / avplayextension path.
 */
export function isTizenVersionTEP(tizenVersion) {
  return tizenVersion != null && tizenVersion > 6;
}

/**
 * Runtime check: TEP IPTV player API (avplayextension).
 */
export function hasTizenTEPAvPlayAPI() {
  try {
    return !!(
      window.webapis &&
      window.webapis.avplay &&
      typeof window.webapis.avplay.open === 'function'
    );
  } catch (e) {
    return false;
  }
}

/**
 * Runtime check: legacy H.Browser / AU8000 player API (b2bavplay).
 */
export function hasLegacyTizenEpAvPlayAPI() {
  try {
    return !!(
      window.webapis &&
      window.webapis.epavplay &&
      typeof window.webapis.epavplay.open === 'function'
    );
  } catch (e) {
    return false;
  }
}

/**
 * Decide TEP vs legacy when UA alone is ambiguous (e.g. stored flags from old builds).
 * Prefer avplay when both exist; prefer epavplay only when avplay is missing.
 */
export function shouldUseTizenTEPPlayer() {
  var tizenVersion = getTizenVersionFromUA();
  if (isTizenVersionTEP(tizenVersion)) {
    return true;
  }
  if (hasTizenTEPAvPlayAPI() && !hasLegacyTizenEpAvPlayAPI()) {
    return true;
  }
  // Both APIs loaded (index.html includes B2B + TEP scripts) — use UA version only.
  // Never default to TEP when version is unknown; AU8000 must stay on epavplay.
  if (hasTizenTEPAvPlayAPI() && hasLegacyTizenEpAvPlayAPI()) {
    return isTizenVersionTEP(tizenVersion);
  }
  return false;
}

/**
 * @param {{ isTIZEN?: boolean, isTIZEN_TEP?: boolean }} deviceList
 */
export function usesTizenAvPlayerSurface(deviceList) {
  if (!deviceList) {
    return false;
  }
  return !!(deviceList.isTIZEN || deviceList.isTIZEN_TEP);
}

/**
 * Normalize IPTV stream URLs for Samsung avplay / epavplay.
 * Some head-ends emit udp://@224.x.x.x — strip the stray @ after the scheme.
 *
 * @param {string} url
 * @returns {string}
 */
export function normalizeTizenStreamUrl(url) {
  if (!url || typeof url !== 'string') {
    return url;
  }
  var trimmed = url.trim();
  // Head-end URLs like udp://@224.2.2.4:3052 — remove @ after scheme
  if (/^udp:\/\/@+/i.test(trimmed)) {
    trimmed = trimmed.replace(/^udp:\/\/@+/i, 'udp://');
  }
  return trimmed;
}
